// Eltern-Portal: Beziehungstypen für Familienmitglieder (Kind, Partner, Oma/Opa).

function __asStr(x) {
  return String(x ?? "").trim();
}

/** @readonly */
export const PARENT_PORTAL_CHILD_RELATION = "child";

/** @readonly */
export const PARENT_PORTAL_ADULT_FAMILY_RELATIONS = Object.freeze([
  "partner",
  "grandfather",
  "grandmother",
]);

/** @readonly */
export const PARENT_PORTAL_FAMILY_RELATION_VALUES = Object.freeze([
  PARENT_PORTAL_CHILD_RELATION,
  ...PARENT_PORTAL_ADULT_FAMILY_RELATIONS,
]);

const LEGACY_RELATION_ALIASES = Object.freeze({
  family: "partner",
  spouse: "partner",
  husband: "partner",
  wife: "partner",
  ehemann: "partner",
  ehefrau: "partner",
  mann: "partner",
  frau: "partner",
  opa: "grandfather",
  oma: "grandmother",
  grandparent: "grandfather",
});

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeParentPortalFamilyRelation(value) {
  const raw = __asStr(value).toLowerCase();
  if (!raw) return PARENT_PORTAL_CHILD_RELATION;
  if (PARENT_PORTAL_FAMILY_RELATION_VALUES.includes(raw)) return raw;
  return LEGACY_RELATION_ALIASES[raw] || raw;
}

/**
 * @param {unknown} value
 */
export function isParentPortalFamilyRelation(value) {
  return PARENT_PORTAL_FAMILY_RELATION_VALUES.includes(
    normalizeParentPortalFamilyRelation(value),
  );
}

/**
 * @param {unknown} value
 */
export function isParentPortalChildRelation(value) {
  return (
    normalizeParentPortalFamilyRelation(value) === PARENT_PORTAL_CHILD_RELATION
  );
}

/**
 * @param {unknown} value
 */
export function isParentPortalAdultFamilyRelation(value) {
  return PARENT_PORTAL_ADULT_FAMILY_RELATIONS.includes(
    normalizeParentPortalFamilyRelation(value),
  );
}

/**
 * @param {unknown} relation
 * @param {unknown} birthDate
 */
export function parentPortalFamilyCreateRequiresEmail(relation, birthDate) {
  if (isParentPortalAdultFamilyRelation(relation)) return true;
  const raw = __asStr(birthDate);
  if (!raw) return false;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age -= 1;
  }
  return age >= 14;
}

/**
 * i18n-Key für Beziehung im Anlege-Formular.
 * @param {unknown} relation
 */
export function parentPortalFamilyRelationOptionLabelKey(relation) {
  const canon = normalizeParentPortalFamilyRelation(relation);
  if (PARENT_PORTAL_FAMILY_RELATION_VALUES.includes(canon)) {
    return `trainerstunden.family.dependents.relation.${canon}`;
  }
  return "trainerstunden.family.dependents.relation.family";
}

/**
 * i18n-Key für Profil-Sektionstitel (Familie-Tab).
 * @param {unknown} relation
 * @param {{ isViewer?: boolean, isSelf?: boolean }} [opts]
 */
export function parentPortalFamilyProfileRoleLabelKey(
  relation,
  { isViewer = false, isSelf = false } = {},
) {
  if (isViewer) return "trainerstunden.family.profile.roleParent";
  if (isSelf) return "trainerstunden.family.profile.sectionSelf";
  const canon = normalizeParentPortalFamilyRelation(relation);
  if (canon === PARENT_PORTAL_CHILD_RELATION) {
    return "trainerstunden.family.profile.roleChild";
  }
  if (PARENT_PORTAL_ADULT_FAMILY_RELATIONS.includes(canon)) {
    return `trainerstunden.family.profile.role.${canon}`;
  }
  return "trainerstunden.family.profile.roleChild";
}

/**
 * @param {object|null|undefined} row
 */
export function resolveParentPortalFamilyMemberRelation(row) {
  return normalizeParentPortalFamilyRelation(
    row?.relationToContact ??
      row?.relation_to_contact ??
      row?.relationshipType ??
      row?.relationship_type,
  );
}
